/**
 * What the coach has seen happen on the course (ROUND7 §5). Some lessons are about a moment that has passed -
 * the pod he kicked, the man he knifed and whether that man knew - so the coach keeps a small record, updated
 * from the world and the tick's events after every sim step. It only reads; it never writes to the world.
 */

import { COURSE, COURSE_IDS, trainingCourse } from '../../gen/training';
import { isAware } from '../../sim/ranger/knife';
import { waterAt } from '../../sim/statics';
import type { SimEvent, World } from '../../sim/types';

/** The stab's own rule, from the sim: a knife kill on a man who knew he was there is loud. */
export { isAware };

/** The course as built: the sim's own answer to "is he in water here?" is asked of it. */
const GROUND = trainingCourse();

export interface Facts {
  kicked: boolean;
  jumped: boolean;
  steered: boolean;
  /** How far he had walked and where he aimed on his first tick on the ground. */
  landed: { travelled: number; aim: number } | null;
  runTicks: number;
  sneakTicks: number;
  crawled: boolean;
  stoodAfterCrawl: boolean;
  podOpened: boolean;
  silentKills: number;
  loudKills: number;
  /** Crawled in the trench level with or past the watcher. */
  crawledPast: boolean;
  /** ROUND8 §5: stood in the stream's water - the only water on the course - and then came out north of it. */
  waded: boolean;
  crossedStream: boolean;
  /** A dog ran his trail (`track`), or had him and gave tongue (`bay`), at some moment. */
  dogTracked: boolean;
  dogBayed: boolean;
  lampOut: boolean;
  crateBlown: boolean;
  wallBreached: boolean;
  recalled: boolean;
  boarded: boolean;
  /** Each soldier's awareness on the previous tick: a stab is judged on what he knew before it. */
  aware: Map<number, boolean>;
}

export function newFacts(): Facts {
  return {
    kicked: false, jumped: false, steered: false, landed: null, runTicks: 0, sneakTicks: 0,
    crawled: false, stoodAfterCrawl: false, podOpened: false, silentKills: 0, loudKills: 0, crawledPast: false,
    waded: false, crossedStream: false, dogTracked: false, dogBayed: false,
    lampOut: false, crateBlown: false, wallBreached: false, recalled: false, boarded: false, aware: new Map(),
  };
}

/** Steering counts once the canopy has turned this far off its line, radians. */
const STEERED_AT = 0.25;

function readEvents(f: Facts, events: readonly SimEvent[]): void {
  const stabbed = events.some((e) => e.type === 'knife' && e.hit);
  for (const e of events) {
    if (e.type === 'podDropped') f.kicked = true;
    else if (e.type === 'jump') f.jumped = true;
    else if (e.type === 'podOpened') f.podOpened = true;
    else if (e.type === 'osprey' && e.phase === 'inbound') f.recalled = true;
    else if (e.type === 'enemyHit' && e.killed && stabbed) {
      if (f.aware.get(e.id) === true) f.loudKills++;
      else f.silentKills++;
    } else if (e.type === 'propDestroyed') {
      if (COURSE_IDS.lamps.includes(e.id)) f.lampOut = true;
      if (COURSE_IDS.crates.includes(e.id)) f.crateBlown = true;
      if (e.id === COURSE_IDS.wallTarget) f.wallBreached = true;
    }
  }
}

/** The dog station (ROUND8 §5): his feet in the water, and what any dog on the course is doing about him. */
function readDogStation(f: Facts, w: World): void {
  const r = w.ranger;
  if (waterAt(GROUND, r.x, r.z)) f.waded = true;
  if (f.waded && r.z < COURSE.dogs.stream.north) f.crossedStream = true;
  for (const e of w.enemies) {
    if (e.kind !== 'dog' || e.state === 'dead' || !e.dog) continue;
    if (e.dog.mode === 'track') f.dogTracked = true;
    if (e.dog.mode === 'bay') f.dogBayed = true;
  }
}

function readWorld(f: Facts, w: World): void {
  const r = w.ranger;
  const ins = w.insertion;
  if (ins.phase === 'descent' && Math.abs(ins.heading) > STEERED_AT) f.steered = true;
  if (ins.phase !== 'ground') return;
  if (f.landed === null) f.landed = { travelled: r.travelled, aim: r.aim };
  if (r.running) f.runTicks++;
  if (r.sneaking === true && r.moving && r.stance === 'stand' && !r.running) f.sneakTicks++;
  if (r.stance === 'crawl') f.crawled = true;
  else if (f.crawled) f.stoodAfterCrawl = true;
  if (r.inTrench && r.stance === 'crawl' && r.z <= COURSE.watcher.z) f.crawledPast = true;
  readDogStation(f, w);
  if (r.status === 'boarding' || r.status === 'extracted') f.boarded = true;
}

/** One sim step's worth: the events first (judged against last tick's awareness), then the world as it stands. */
export function observe(f: Facts, w: World, events: readonly SimEvent[]): void {
  readEvents(f, events);
  readWorld(f, w);
  f.aware.clear();
  for (const e of w.enemies) f.aware.set(e.id, isAware(e));
}
